import { ColumnDef } from '@tanstack/react-table';
import { AudioTrackCard } from '@/components/common/AudioCard';
import { InformationSection } from '@/components/common/InformationSection';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useHeader } from '@/components/context/HeaderContext';
import {
  useGetContentByIdQuery,
  useStreamContentQuery,
  useUpdateContentMutation,
} from '@/store/api/contentApi';
import { useUploadFileMutation } from '@/store/api/uploadApi';
import { getLockedContentPath } from '@/utils/contentRoutes';
import FullScreenLoader from '@/components/common/FullScreenLoader';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { createContentSchema } from '@/utils/validation';

export type ContentData = {
  id: string;
  userEmail: string;
  avatarUrl: string;
  dateTime: string;
  duration: string;
  playCount: number;
};

export const columns: ColumnDef<ContentData>[] = [
  {
    accessorKey: 'user',
    header: 'User',
    cell: ({ row }) => (
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-[#1E0036] flex-shrink-0 overflow-hidden border border-[#F0EEF9]">
          <img
            src={row.original.avatarUrl || '/api/placeholder/40/40'}
            alt="User"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="font-semibold text-[16px] text-[#1E0036]">{row.original.userEmail}</div>
      </div>
    ),
  },
  {
    accessorKey: 'dateTime',
    header: 'Date & Time',
    cell: ({ row }) => <span className="font-bold text-[#1A1A1A]">{row.original.dateTime}</span>,
  },
  {
    accessorKey: 'duration',
    header: 'Duration',
    cell: ({ row }) => <span className="font-bold text-[#1A1A1A]">{row.original.duration}</span>,
  },
  // {
  //   accessorKey: 'playCount',
  //   header: 'Play Count',
  //   cell: ({ row }) => (
  //     <div className="font-bold text-[#1A1A1A] text-center w-full max-w-[80px]">
  //       {row.original.playCount.toString().padStart(2, '0')}
  //     </div>
  //   ),
  // },
];
const LockedContentDetail = () => {
  const { setHeader } = useHeader();
  const { contentId } = useParams<{ contentId: string }>();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [isAudioUploading, setIsAudioUploading] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewIconUrl, setPreviewIconUrl] = useState<string | null>(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [streamedAudioUrl, setStreamedAudioUrl] = useState<string | null>(null);
  const contentIdNumber = Number(contentId);
  const { data: content, isLoading } = useGetContentByIdQuery(contentIdNumber, {
    skip: Number.isNaN(contentIdNumber),
  });
  const { refetch: streamContent } = useStreamContentQuery(contentIdNumber, {
    skip: !contentIdNumber,
  });
  const sanitize = (value?: string) => value?.trim().replace(/\s+/g, ' ') ?? '';
  const sanitizeOptional = (value?: string) => {
    const sanitized = sanitize(value);
    return sanitized ? sanitized : null;
  };
  const sanitizeUrl = (value?: string) => {
    const sanitized = value?.trim() ?? '';
    return sanitized ? sanitized : null;
  };
  const isLockedContent = content?.category?.isLocked === true;
  const [updateContent] = useUpdateContentMutation();
  const [uploadFile] = useUploadFileMutation();

  const resolveAssetUpload = async (
    value: string | File | null | undefined,
    kind: 'image' | 'icon'
  ) => {
    if (value instanceof File) {
      const uploadResponse = await uploadFile({ file: value, kind }).unwrap();

      return {
        url:
          uploadResponse.meta.imageUrl ||
          uploadResponse.meta.iconUrl ||
          uploadResponse.meta.url ||
          uploadResponse.meta.fileUrl ||
          null,
        meta: uploadResponse.meta,
      };
    }

    if (typeof value === 'string') {
      const sanitized = value.trim();

      return {
        url: sanitized || null,
        meta: null,
      };
    }

    return {
      url: null,
      meta: null,
    };
  };
  const uploadAudioSource = async (value: File) => {
    setIsAudioUploading(true);

    try {
      const uploadResponse = await uploadFile({ file: value }).unwrap();

      return {
        audioUrl: uploadResponse.meta.url || uploadResponse.meta.fileUrl || null,
        audioKey: uploadResponse.meta.audioKey || null,
        duration: uploadResponse.meta.duration ?? null,
        audioUploadToken: uploadResponse.meta.uploadToken || null,
      };
    } finally {
      setIsAudioUploading(false);
    }
  };

  useEffect(() => {
    const saveButton = sectionRef.current?.querySelector<HTMLButtonElement>('button.bg-darkPurple');

    if (!saveButton) return;

    saveButton.textContent = isAudioUploading ? 'Uploading...' : 'Save';
    saveButton.disabled = isAudioUploading;
  }, [isAudioUploading]);

  useEffect(() => {
    if (!content || !isLockedContent) return;

    setHeader({
      type: 'content',
      props: {
        title: content.title,
        breadcrumbs: [
          { label: 'Home', href: '/' },
          { label: 'Locked Content', href: '/locked-content' },
          { label: content.title, href: getLockedContentPath(content.id) },
        ],
        onBack: () => window.history.back(),
        // lastUpdated: '14-April-2026, 3:00PM',
        // rightContent: <MoreVertical />,
      },
    });

    return () => setHeader(null);
  }, [content, isLockedContent, setHeader]);

  if (isLoading)
    return (
      <div className="p-6">
        <FullScreenLoader />
      </div>
    );

  if (!content || !isLockedContent)
    return (
      <div>
        <NotFoundPage />
      </div>
    );

  return (
    <div ref={sectionRef} className="p-3 sm:p-6 bg-[#F8F9FB] min-h-screen space-y-6">
      <InformationSection
        title="Content Information"
        editable
        className="bg-white"
        validationSchema={createContentSchema(true).pick({
          title: true,
          benefits: true,
          sourceType: true,
          productLink: true,
          personalizedProductLink: true,
          sanskritMantra: true,
          englishTranslation: true,
          englishPronunciation: true,
          imageUrl: true,
          iconUrl: true,
        })}
        onSave={async (values) => {
          const [imageUpload, iconUpload] = await Promise.all([
            resolveAssetUpload(values.imageUrl, 'image'),
            resolveAssetUpload(values.iconUrl, 'icon'),
          ]);

          const audioUpload =
            values.file instanceof File ? await uploadAudioSource(values.file) : null;
          let shouldRefreshStream = false;
          if (audioUpload) {
            shouldRefreshStream = true;
          }
          await updateContent({
            id: content.id,

            title: sanitize(values.title) || content.title,

            benefits: sanitizeOptional(values.benefits) || content.benefits,

            sourceType: sanitizeOptional(values.sourceType) || content.sourceType,

            productLink: sanitizeUrl(values.productLink) || content.productLink,
            personalizedProductLink:
              sanitizeUrl(values.personalizedProductLink) || content.personalizedProductLink,

            sanskritMantra: values.sanskritMantra || content.sanskritMantra,

            englishTranslation: values.englishTranslation || content.englishTranslation,

            englishPronunciation: values.englishPronunciation || content.englishPronunciation,

            imageUrl: imageUpload.url,
            iconUrl: iconUpload.url,

            audioUrl: audioUpload?.audioUrl,
            audioKey: audioUpload?.audioKey,
            duration: audioUpload?.duration,
            audioUploadToken: audioUpload?.audioUploadToken,
          }).unwrap();
          if (shouldRefreshStream) {
            const streamResponse = await streamContent();

            if (streamResponse.data?.audioUrl) {
              setStreamedAudioUrl(streamResponse.data.audioUrl);
            }
          }
          if (imageUpload.url) {
            setPreviewImageUrl(imageUpload.url);
          }

          if (iconUpload.url) {
            setPreviewIconUrl(iconUpload.url);
          }

          if (audioUpload?.audioUrl) {
            setPreviewAudioUrl(audioUpload.audioUrl);
          }
        }}
        fields={[
          {
            id: 'title',
            label: 'Title',
            defaultValue: content.title,
          },
          // {
          //   id: 'benefits',
          //   label: 'Benefits',
          //   defaultValue: content.benefits || '',
          // },

          {
            id: 'sourceType',
            label: 'Best Time To Listen',
            defaultValue: content.sourceType || '',
          },

          {
            id: 'productLink',
            label: 'Product Link',
            type: 'url',
            defaultValue: content.productLink || '',
          },
          {
            id: 'personalizedProductLink',
            label: 'Personal Product Link',
            type: 'url',
            defaultValue: content.personalizedProductLink || '',
          },
          // {
          //   id: 'description',
          //   label: 'Description',
          //   type: 'textarea',
          //   defaultValue: content.description || '',
          // },

          {
            id: 'sanskritMantra',
            label: 'Sanskrit Mantra',
            type: 'textarea',
            defaultValue: content.sanskritMantra || '',
          },

          {
            id: 'englishTranslation',
            label: 'English Translation',
            type: 'textarea',
            defaultValue: content.englishTranslation || '',
          },

          {
            id: 'englishPronunciation',
            label: 'English Pronunciation',
            type: 'textarea',
            defaultValue: content.englishPronunciation || '',
          },
          {
            id: 'imageUrl',
            label: 'Thumbnail Image',
            type: 'file',
            accept: 'image/*',
            defaultValue: previewImageUrl || content.imageUrl || null,
          },

          {
            id: 'iconUrl',
            label: 'Mantra Icon',
            type: 'file',
            accept: 'image/*',
            defaultValue: previewIconUrl || content.iconUrl || null,
          },

          // Audio fields
          {
            id: 'audioName',
            label: 'Audio Name',
            defaultValue: content.audioKey?.split('/').pop() || 'No audio',
            editable: false,
          },

          {
            id: 'audioPreview',
            label: 'Listen Audio',
            type: 'custom' as const,
            editable: false,
            render: () => {
              const audioSrc =
                streamedAudioUrl ||
                previewAudioUrl ||
                content.cachedAudioUrl ||
                content.audioUrl ||
                '';

              return (
                <audio key={audioSrc} controls className="w-full">
                  <source src={audioSrc} />
                  Your browser does not support audio.
                </audio>
              );
            },
          },
          {
            id: 'file',
            label: 'Replace Audio',
            type: 'file',
            accept: 'audio/*',
            defaultValue: null,
          },

          // {
          //   id: 'duration',
          //   label: 'Duration',
          //   defaultValue: `${content.duration ?? 0} sec`,
          //   editable: false,
          // },

          // {
          //   id: 'playCount',
          //   label: 'Play Count',
          //   defaultValue: String(content.playCount ?? 0),
          //   editable: false,
          // },
        ]}
      />
    </div>
  );
};

export default LockedContentDetail;
